import React, { useState } from "react";
import { Formik, Form } from "formik";
import { useMutate, useGet } from "restful-react";
import { connect } from "react-redux";
import { useSnackbar } from "notistack";
import intl from "react-intl-universal";
import { useRouteMatch } from "react-router-dom";
import { Grid, Box, Button, Divider } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { omit, remove } from "lodash-es";

import { routing } from "routing";
import {
  postWithballData,
  IPostWithballDataResponse,
  IMatchResponse,
  getMatch,
  IWithballData,
  getMatchWithballlData,
  IMatchWithballDataResponse,
  deleteWithballData,
} from "apis";
import {
  startLoadingAction,
  stopLoadingAction,
  ReduxSpinnerProps,
} from "components/ReduxSpinner";
import { VideoPlayer } from "components/VideoPlayer";
import { flipPosition } from "utils";

import { FIELD, POSSESSION_TYPE, EVENT_VALUES } from "./constants";
import { TEAM_TYPE, HOME_TEAM_SIDE, IStatsValue } from "./types";

import { PlayerToolbar } from "./PlayerToolbar";
import { DataEditor } from "./DataEditor";
import { HotKeys } from "./HotKeys";
import { WithballDataList } from "./WithballDataList";
import { EditorGrid } from "./DataEditor/EditorGrid";
import { MatchResultForm } from "./MatchResultForm";

const { Urls } = routing;

interface WithballDataInputProps extends ReduxSpinnerProps {}

interface MatchParams {
  matchId: string;
}

const useStyles = makeStyles(() => ({
  submit: {
    width: "100%",
  },
}));

export const WithballDataInputComp = ({
  startLoading,
  stopLoading,
}: WithballDataInputProps) => {
  const classes = useStyles();
  const match = useRouteMatch<MatchParams>({
    path: Urls.WITHBALL_DATA_INPUT_URL_PATTERN,
    strict: true,
  });

  const [dataList, setDataList] = useState<IWithballData[]>([]);

  const matchId = match?.params.matchId;

  const { data: matchResponse } = useGet<IMatchResponse>({
    path: matchId ? getMatch(parseInt(matchId)) : "",
  });

  useGet<IMatchWithballDataResponse>({
    path: matchId ? getMatchWithballlData(matchId) : "",
    resolve: (data: IMatchWithballDataResponse) => {
      if (data.withballData) {
        setDataList(data.withballData);
      }
      return data;
    },
  });

  const { mutate: postData } = useMutate({
    verb: "POST",
    path: postWithballData(),
  });

  const { mutate: deleteData } = useMutate({
    verb: "DELETE",
    path: deleteWithballData(),
  });

  const { enqueueSnackbar } = useSnackbar();

  const [url, setUrl] = useState("");

  const [currentTime, setCurrentTime] = useState(0.0);

  const handleUrlChange = (url: string) => {
    setUrl(url);
  };

  const handleDataDelete = (removedData: IWithballData) => {
    startLoading();
    deleteData(removedData.id)
      .then(() => {
        remove(dataList, (item: IWithballData) => item.id === removedData.id);
        setDataList([...dataList]);
      })
      .catch(() => {
        enqueueSnackbar(intl.get("STAT_NOTIFICATION_WITHBALL_DELETE_FAILED"), {
          variant: "error",
        });
      })
      .finally(() => stopLoading());
  };

  const handleProgressChange = (currentTime: number) => {
    setCurrentTime(currentTime);
  };

  const initialValues = {
    [FIELD.teamType]: TEAM_TYPE.HOME,
    [FIELD.action]: "PASS",
    [FIELD.method]: 1,
    [FIELD.bodyPart]: 1,
    [FIELD.threat]: false,
    [FIELD.succeed]: true,
    [FIELD.possession]: POSSESSION_TYPE.keep,
    [FIELD.homeTeamSide]: HOME_TEAM_SIDE.LEFT,
    [FIELD.fromPlayer]: -1,
    [FIELD.toPlayer]: -1,
    [FIELD.createdAt]: 0,
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <PlayerToolbar onUrlChange={handleUrlChange} />
          <VideoPlayer url={url} onProgress={handleProgressChange} />
          {/* <VideoStatusBar /> */}
          <Box mt={2} />
          <WithballDataList
            data={dataList}
            homeTeam={matchResponse?.match.homeFullTeam}
            awayTeam={matchResponse?.match.awayFullTeam}
            onDataDelete={handleDataDelete}
          />
        </Grid>
        <Grid item xs={4}>
          {/* <DateTimePicker /> */}
          <Formik
            initialValues={initialValues}
            onSubmit={async (values, { resetForm }) => {
              startLoading();

              const adaptedFromPosition = flipPosition(
                {
                  x: values[FIELD.fromX] as number,
                  y: values[FIELD.fromY] as number,
                },
                values[FIELD.teamType] === TEAM_TYPE.HOME,
                values[FIELD.homeTeamSide] === HOME_TEAM_SIDE.LEFT
              );
              const adaptedToPosition = flipPosition(
                {
                  x: values[FIELD.toX] as number,
                  y: values[FIELD.toY] as number,
                },
                values[FIELD.teamType] === TEAM_TYPE.HOME,
                values[FIELD.homeTeamSide] === HOME_TEAM_SIDE.LEFT
              );

              const input = {
                ...omit(values, [
                  FIELD.teamType,
                  FIELD.homeTeamSide,
                  FIELD.action,
                ]),
                id: -1, // for new created
                [FIELD.action]:
                  EVENT_VALUES[values[FIELD.action] as IStatsValue],
                [FIELD.matchId]: matchId,
                [FIELD.teamId]:
                  values[FIELD.teamType] === TEAM_TYPE.HOME
                    ? matchResponse?.match.homeFullTeam?.id || -1
                    : matchResponse?.match.awayFullTeam?.id || -1,
                [FIELD.fromX]: adaptedFromPosition.x,
                [FIELD.fromY]: adaptedFromPosition.y,
                [FIELD.toX]: adaptedToPosition.x,
                [FIELD.toY]: adaptedToPosition.y,
                [FIELD.createdAt]: currentTime,
              };

              postData(input)
                .then((response: IPostWithballDataResponse) => {
                  if (response.errCode > 0) {
                    enqueueSnackbar(response.errMessage, {
                      variant: "error",
                    });
                    return;
                  }

                  if (response.withballData) {
                    setDataList([response.withballData, ...dataList]);
                    enqueueSnackbar(
                      intl.get("STAT_NOTIFICATION_WITHBALL_POST_SUCCESS"),
                      {
                        variant: "success",
                        autoHideDuration: 1000,
                      }
                    );

                    resetForm({
                      values: {
                        ...initialValues,
                        [FIELD.teamType]: values[FIELD.teamType],
                        [FIELD.homeTeamSide]: values[FIELD.homeTeamSide],
                        [FIELD.fromX]: values[FIELD.fromX],
                        [FIELD.fromY]: values[FIELD.fromY],
                        [FIELD.toX]: values[FIELD.toX],
                        [FIELD.toY]: values[FIELD.toY],
                      },
                    });
                  }
                })
                .catch(() => {
                  enqueueSnackbar(
                    intl.get("STAT_NOTIFICATION_WITHBALL_POST_FAILED"),
                    {
                      variant: "error",
                    }
                  );
                })
                .finally(() => stopLoading());
            }}
          >
            {() => {
              return (
                <Form>
                  <DataEditor
                    homeTeam={matchResponse?.match.homeFullTeam}
                    awayTeam={matchResponse?.match.awayFullTeam}
                  />
                  <EditorGrid item xs={12}>
                    <Button
                      variant="contained"
                      color="secondary"
                      type="submit"
                      className={classes.submit}
                    >
                      {`${intl.get("STAT_FORM_LABEL_SUBMIT")} (M)`}
                    </Button>
                  </EditorGrid>
                  <HotKeys />
                </Form>
              );
            }}
          </Formik>
          <Box mt={2} />
          <Divider />
          <Box mt={2} />
          {matchResponse?.match && (
            <MatchResultForm
              scoresFromInput={
                dataList.filter(
                  (each) => each.action === EVENT_VALUES.SHOOT && each.succeed
                ).length
              }
              match={matchResponse.match}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

const mapDispatch = {
  startLoading: () => startLoadingAction,
  stopLoading: () => stopLoadingAction,
};

export const WithballDataInput = connect(
  null,
  mapDispatch
)(WithballDataInputComp);
